/**
 * Theme hook - resolves the initial theme (stored preference → prefers-color-scheme
 * → light), toggles with a View Transition crossfade, and persists the choice to
 * localStorage.
 */
import { useEffect, useState } from "react";
import type { Theme } from '@/exxonim/types';

const STORAGE_KEY = "exxonim-theme";
const LEGACY_STORAGE_KEY = "koro-theme";

/** Document augmented with the View Transitions API (not in every TS lib yet). */
type ViewTransitionHandle = { finished: Promise<unknown> };
type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => ViewTransitionHandle;
};

/** Class on <html> while a theme view-transition is in flight (see globals.css). */
const SWAP_CLASS = "theme-swapping";

/**
 * Swap the active favicon by toggling the `disabled` property on the two
 * <link rel="icon"> tags declared in index.html. Disabled links are ignored
 * by the browser, so toggling which one is active switches the tab favicon
 * without a page reload. This is the technique that actually works in
 * Chrome/Firefox/Safari after page load.
 */
function syncFaviconToBrowserTheme() {
  if (typeof window === "undefined" || !window.matchMedia) return;
  const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const light = document.getElementById("favicon-light") as HTMLLinkElement | null;
  const dark = document.getElementById("favicon-dark") as HTMLLinkElement | null;
  if (light) light.disabled = isDark;
  if (dark) dark.disabled = !isDark;
}

function getStoredTheme(): Theme | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedTheme =
      localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);

    return storedTheme === "dark" || storedTheme === "light"
      ? storedTheme
      : null;
  } catch {
    return null;
  }
}

function getInitialTheme(): Theme {
  if (typeof document === "undefined") {
    return "light";
  }

  const storedTheme = getStoredTheme();

  if (storedTheme) {
    return storedTheme;
  }

  // The blocking script in index.html has already resolved the theme
  // (saved preference, else prefers-color-scheme, else light).
  return document.documentElement.dataset.theme === "dark" ? "dark" : "light";
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;

    syncFaviconToBrowserTheme();

    if (typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => syncFaviconToBrowserTheme();
      if (mq.addEventListener) {
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
      } else if ((mq as MediaQueryList).addListener) {
        (mq as MediaQueryList).addListener(handler);
        return () => (mq as MediaQueryList).removeListener(handler);
      }
    }
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Ignore storage failures and keep the active theme in memory.
    }
  }, [theme]);

  return {
    theme,
    toggleTheme: () => {
      const next: Theme = theme === "dark" ? "light" : "dark";
      const root = document.documentElement;

      // data-theme drives every colour token in CSS. Set it imperatively (not
      // only via React state) so the change is applied synchronously - that's
      // what lets the View Transition capture the new colours in its callback.
      const applyTheme = () => {
        root.dataset.theme = next;
        setTheme(next);
      };

      const doc = document as ViewTransitionDocument;
      const prefersReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      // View Transitions API: the browser crossfades a snapshot of the whole
      // page on the compositor - uniform AND cheap (no per-element transitions).
      // Falls back to an instant swap where unsupported (Firefox) or reduced-motion.
      if (typeof doc.startViewTransition === "function" && !prefersReducedMotion) {
        // Suppress per-element colour transitions for the duration of the swap
        // (SWAP_CLASS → transition:none in globals.css). Otherwise elements with
        // their own transition-colors/transition-all (blog card borders, links,
        // inputs) would still show the OLD colour when the view transition
        // snapshots the new state, then finish changing after the crossfade -
        // appearing to switch a beat late / inconsistently.
        root.classList.add(SWAP_CLASS);
        const clearSwap = () => root.classList.remove(SWAP_CLASS);
        const transition = doc.startViewTransition(applyTheme);
        transition.finished.finally(clearSwap);
        // Safety net: if `finished` never settles (some engines/headless), remove
        // the class anyway so `transition: none` is never left on the page -
        // otherwise all hover/colour transitions would be dead after one toggle.
        setTimeout(clearSwap, 500);
      } else {
        applyTheme();
      }
    },
  };
}
